// 部署后旧 chunk 失效兜底：
// dist 的构建是覆盖式的（Vite emptyOutDir 会先清空 dist 再生成），所有懒加载 chunk
// 文件名都带内容 hash，每次构建都会变化。重新部署后，服务器上的旧 chunk 已不存在，
// 而停留在旧页面的用户点击导航时，动态 import() 请求的仍是旧 hash 文件，会得到 404，
// 表现为页面卡死/白屏。这里全局监听 chunk 加载失败，自动刷新一次页面，
// 让浏览器重新拉取新 index.html 与对应的新 chunk。
//
// 不能无限自动刷新：若部署尚未完成（新文件还没全部上传），刷新后依然 404，
// 用 sessionStorage 记录上次自动刷新时间，冷静期内再次失败就只提示用户稍后手动刷新。

import type { Router } from "vue-router";

// MessagePlugin 属于 vendor-tdesign chunk，该 chunk 是入口静态依赖、首屏已加载，
// 用户停留在旧页面时必然已在内存中，这里可以安全使用
import { MessagePlugin } from "tdesign-vue-next";

// 各浏览器动态 import 失败时的报错文案
const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module", // Chromium 系（Chrome / Edge）
  "error loading dynamically imported module", // Firefox
  "Importing a module script failed", // Safari
];

// sessionStorage 键：记录上次因 chunk 失败自动刷新的时间戳
const RETRY_FLAG_KEY = "didaoj-chunk-retry-at";

// 自动刷新后的冷静期：期限内再次失败不再自动刷新，避免部署中途陷入刷新死循环
const RETRY_COOLDOWN_MS = 60 * 1000;

// 防重入：同一次 chunk 失败可能同时命中三路监听（preload 失败、import 失败、router onError），
// 只安排一次刷新
let reloadScheduled = false;

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    return CHUNK_ERROR_PATTERNS.some((pattern) => error.message.includes(pattern));
  }
  return false;
}

function handleChunkLoadError() {
  if (reloadScheduled) {
    return;
  }

  const lastRetryAt = Number(sessionStorage.getItem(RETRY_FLAG_KEY) ?? "0");
  const now = Date.now();

  if (now - lastRetryAt < RETRY_COOLDOWN_MS) {
    // 刚自动刷新过仍然失败：新版本可能还未完全就绪（部署进行中），只提示不再自动刷新
    console.warn("[chunk-reload] chunk load failed again after auto reload, give up auto retry");
    try {
      MessagePlugin.warning("站点正在更新，请稍后手动刷新页面", 5000);
    } catch (e) {
      console.warn("[chunk-reload] failed to show tips", e);
    }
    return;
  }

  reloadScheduled = true;
  sessionStorage.setItem(RETRY_FLAG_KEY, String(now));
  console.warn("[chunk-reload] chunk load failed, reloading page to fetch new assets");
  try {
    MessagePlugin.warning("站点已更新，即将自动刷新", 1500);
  } catch (e) {
    console.warn("[chunk-reload] failed to show tips", e);
  }
  // 留出提示展示时间再刷新
  setTimeout(() => {
    location.reload();
  }, 1500);
}

export const initChunkReloadGuard = (router?: Router) => {
  // 开发环境由 dev server 兜底（无 hash 命名、文件始终存在），不启用
  if (!import.meta.env.PROD) {
    return;
  }

  // 1. Vite 预加载失败：preload helper 在 <link rel="modulepreload"> 请求失败时
  //    派发该事件，默认行为是直接抛错；preventDefault 后交给统一处理
  window.addEventListener("vite:preloadError", (event: Event) => {
    event.preventDefault();
    handleChunkLoadError();
  });

  // 2. 动态 import 失败：未被路由/组件捕获时以 unhandledrejection 形式冒出
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      handleChunkLoadError();
    }
  });

  // 3. vue-router 捕获的懒加载路由组件加载失败
  router?.onError((error) => {
    if (isChunkLoadError(error)) {
      handleChunkLoadError();
      return;
    }
    // 非 chunk 错误保持与 vue-router 默认行为一致：打印出来即可
    console.error(error);
  });
};
